import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Plus, Send, ChevronDown, Trash2, Coffee, UtensilsCrossed, Moon, Apple, Edit3, Sparkles, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { format, addDays, isToday, isYesterday, isTomorrow, startOfDay, endOfDay, differenceInCalendarDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Loader } from '@/components/Loader';
import { useDayMeals, deleteMeal, deleteMealItem } from '@/hooks/useMeals';
import { useTelegram } from '@/hooks/useTelegram';
import { useNutrients, type NutrientsData } from '@/hooks/useNutrients';
import { NutrientsBadge } from '@/components/NutrientsModal';
import { MealBreakdownModal } from '@/components/MealBreakdown';
import { DayStrip } from '@/components/DayStrip';
import { dateByOffset } from '@/lib/mealDay';

// Разбор приёма по нутриентам: «Разобрать обед», а не «Узнать пользу продуктов».
// Родительный падеж по типу приёма — тот же принцип, что у «Поделиться обедом»:
// конкретика цепляет сильнее безличной подписи (решение Славы 05.09.2026).
const BREAKDOWN_TITLE: Record<string, string> = {
  breakfast: 'Разбор завтрака',
  lunch: 'Разбор обеда',
  dinner: 'Разбор ужина',
  snack: 'Разбор перекуса',
};
const BREAKDOWN_CTA: Record<string, string> = {
  breakfast: 'Разобрать завтрак',
  lunch: 'Разобрать обед',
  dinner: 'Разобрать ужин',
  snack: 'Разобрать перекус',
};
import { enrichMealNutrients } from '@/hooks/useMeals';
import { supabase } from '@/lib/supabase';
import { signThumbs } from '@/lib/mealThumb';
import { EditItemModal } from '@/components/EditItemModal';
import { SendToPartnerSheet } from '@/components/SendToPartnerSheet';
import type { Partner } from '@/hooks/usePartners';
import { useUserStore } from '@/store/userStore';
import { ShareMealButton } from '@/components/ShareMealButton';
import { MealHintBlock } from '@/components/MealHintBlock';
import { mealTypeLabel, fmtNum } from '@/utils/format';
import type { MealType, Meal, MealComponent } from '@/types';
import { motion, AnimatePresence } from 'framer-motion';
const mealIcons: Record<MealType, typeof Coffee> = {
  breakfast: Coffee,
  lunch: UtensilsCrossed,
  dinner: Moon,
  snack: Apple,
};
const mealColors: Record<MealType, string> = {
  breakfast: 'text-warn',
  lunch: 'text-emerald-glow',
  dinner: 'text-sky-glow',
  snack: 'text-ink-muted',
};
// Сколько дней назад показывает полоска дневника; раньше — через календарь
const DIARY_BACK_DAYS = 30;

export default function Diary() {
  const nav = useNavigate();
  const [date, setDate] = useState(new Date());
  const { meals, totals, loading, refetch } = useDayMeals(date);
  const { haptic } = useTelegram();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // 🆕 Состояние развёрнутых components внутри items (ключ: item.id)
  const [expandedComponents, setExpandedComponents] = useState<Record<string, boolean>>({});
  const [nutrientsModal, setNutrientsModal] = useState<{
    open: boolean;
    mealName: string;
    data: NutrientsData | null;
    meal: Meal | null;
  }>({ open: false, mealName: '', data: null, meal: null });
  const [editingItem, setEditingItem] = useState<any>(null);
  // Партнёры «Едим вместе» — одним запросом на экран. usePartners тут не берём:
  // он ещё и опрашивает входящие, а дневнику нужен только список, кому отправить.
  const [partners, setPartners] = useState<Partner[]>([]);
  // Окно живёт на уровне экрана, а не внутри карточки приёма: у карточек размытый
  // фон (backdrop-filter), и окно с position: fixed обрезалось бы их границами.
  const [sendSheet, setSendSheet] = useState<{ type: MealType; mealId: string | null } | null>(null);
  // Открыто ли — отдельно: при закрытии список должен доехать вместе с окном, а не опустеть
  const [sendOpen, setSendOpen] = useState(false);
  // Ждём профиль: без готовой сессии сервер ответит отказом, и кнопки отправки
  // не будет до следующего открытия экрана. Возврат в приложение — перечитываем.
  const profileId = useUserStore((s) => s.profile?.id);
  useEffect(() => {
    if (!profileId) return;
    const load = () =>
      supabase.rpc('my_partners').then(({ data, error }) => {
        if (error) { console.error('[Diary] partners', error); return; }
        setPartners((data as Partner[]) ?? []);
      });
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [profileId]);
  // Ссылки на превью выдаются подписанными и на время: корзина закрытая,
  // чтобы снимки еды нельзя было открыть по угаданному адресу.
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  useEffect(() => {
    const paths = (meals ?? []).map((m: any) => m.photo_url).filter(Boolean);
    if (!paths.length) {
      setThumbs({});
      return;
    }
    signThumbs(paths).then(setThumbs).catch(() => setThumbs({}));
  }, [meals]);
  // Вперёд листаем на два дня всегда — чтобы запланировать еду прямо из дневника
  // (07.10.2026), и дальше, если записи там уже есть.
  // До 05.10 стрелка «вперёд» была выключена на сегодняшнем дне: чат уже умел
  // записывать на завтра, а посмотреть записанное было нельзя.
  //
  // Тем же запросом — дни с записями за месяц: точка под числом в полоске дней.
  const [lastPlanned, setLastPlanned] = useState<Date | null>(null);
  const [marks, setMarks] = useState<Set<number>>(new Set());
  useEffect(() => {
    supabase
      .from('meals')
      .select('consumed_at')
      .gte('consumed_at', startOfDay(addDays(new Date(), -DIARY_BACK_DAYS)).toISOString())
      .order('consumed_at', { ascending: false })
      .limit(3000)
      .then(({ data, error }) => {
        if (error) console.error('[Diary] days with meals lookup', error);
        const today = new Date();
        const set = new Set<number>();
        for (const r of data ?? []) set.add(differenceInCalendarDays(new Date(r.consumed_at), today));
        setMarks(set);
        const newest = data?.[0]?.consumed_at ? new Date(data[0].consumed_at) : null;
        setLastPlanned(newest && newest > endOfDay(today) ? newest : null);
      });
    // meals в зависимостях: удалил запланированное — граница должна сдвинуться назад
  }, [meals]);
  const planAhead = startOfDay(addDays(new Date(), 2));
  const forwardLimit = lastPlanned && startOfDay(lastPlanned) > planAhead ? startOfDay(lastPlanned) : planAhead;
  const offset = differenceInCalendarDays(date, new Date());
  const forwardDays = differenceInCalendarDays(forwardLimit, new Date());
  const isFuture = startOfDay(date) > startOfDay(new Date());
  const dateLabel = isToday(date)
    ? 'Сегодня'
    : isYesterday(date)
    ? 'Вчера'
    : isTomorrow(date)
    ? 'Завтра'
    : isFuture
    ? `${format(date, 'd MMM', { locale: ru })} · план`
    : format(date, 'd MMM', { locale: ru });
  const grouped = (
    ['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]
  ).map((mt) => ({
    type: mt,
    items: meals.filter((m) => m.meal_type === mt),
  }));
  function toggleExpand(id: string) {
    haptic('light');
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }
  // 🆕 Разворачивание components у конкретного item
  function toggleExpandComponents(itemId: string) {
    haptic('light');
    setExpandedComponents((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  }
  function addToMealType(mt: MealType) {
    haptic('medium');
    // 📅 Запись идёт в тот день, на который человек смотрит (07.10.2026).
    // Раньше передавалось только «вчера»: открыл завтрашний день, нажал «добавить» —
    // и еда записывалась на сегодня.
    const dateParam = isToday(date) ? '' : `&date=${format(date, 'yyyy-MM-dd')}`;
    nav(`/add?mode=photo&meal_type=${mt}${dateParam}`);
  }
  async function handleDelete(id: string) {
    if (!confirm('Удалить этот приём?')) return;
    haptic('medium');
    await deleteMeal(id);
    refetch();
  }
  async function handleDeleteItem(itemId: string) {
    haptic('medium');
    try {
      await deleteMealItem(itemId);
      refetch();
    } catch (e) {
      console.error(e);
    }
  }
  function openNutrients(meal: Meal, data: NutrientsData | null) {
    const itemsList = (meal.items ?? []).map((i: any) => i.name).join(', ');
    setNutrientsModal({
      open: true,
      mealName: itemsList || mealTypeLabel[meal.meal_type as MealType],
      data,
      meal,
    });
    haptic('light');
  }

  /**
   * Перезапрос, пока досчёт не закончился.
   *
   * Раньше модалка показывала «нутриенты пока считаются» и замирала навсегда,
   * а ниже одновременно писала «значимых нутриентов не выделено» — два
   * противоречащих сообщения и никакого движения. Человек делал вывод, что сломано.
   */
  async function retryNutrients() {
    const meal = nutrientsModal.meal;
    if (!meal) return null;
    const { data } = await supabase.functions.invoke('analyze-nutrients', {
      body: {
        mealId: meal.id,
        items: (meal.items ?? []).map((i: any) => ({
          name: i.name,
          weight_g: Number(i.weight_g) || 100,
        })),
      },
    });
    if (data && !data.pending) {
      setNutrientsModal((m) => ({ ...m, data }));
    }
    return data ?? null;
  }
  return (
    <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
      {/* Выбор дня полоской, как на экране записи (просьба Славы 07.10.2026).
          Стрелки убраны: полоска показывает месяц назад, дальше — календарь. */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs text-ink-dim uppercase tracking-wider">
            {dateLabel}
          </div>
          <div className="font-display text-lg first-letter:uppercase">
            {format(date, 'EEEE, d MMMM', { locale: ru })}
          </div>
        </div>
        {/* Невидимое поле даты поверх кнопки: так системный календарь открывается
            и в Telegram на iOS, где showPicker() не везде есть. */}
        <label className="relative w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center text-ink-muted">
          <CalendarDays size={18} />
          <input
            type="date"
            aria-label="Выбрать дату"
            value={format(date, 'yyyy-MM-dd')}
            max={format(forwardLimit, 'yyyy-MM-dd')}
            onChange={(e) => {
              const [y, m, d] = e.target.value.split('-').map(Number);
              if (!y || !m || !d) return;
              const picked = new Date(y, m - 1, d);
              setDate(picked > forwardLimit ? forwardLimit : picked);
              haptic('light');
            }}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </label>
      </div>
      <div className="mb-5">
        <DayStrip
          value={offset}
          onChange={(o) => setDate(dateByOffset(o))}
          back={DIARY_BACK_DAYS}
          forward={forwardDays}
          marks={marks}
          hint={false}
          bleed
        />
      </div>
      {/* ⭐ Кнопка "Мои блюда" */}
      <button
        type="button"
        onClick={() => {
          haptic('light');
          nav('/favorites');
        }}
        className="w-full mb-4 card p-3 flex items-center gap-3 hover:bg-bg-elevated/40 active:scale-[0.99] transition-all"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-glow/20 to-sky-glow/15 flex items-center justify-center shrink-0">
          <Sparkles size={16} className="text-emerald-glow" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="font-display font-semibold text-sm">Мои блюда</div>
          <div className="text-[11px] text-ink-dim">Рецепты и любимые продукты — добавь одним тапом</div>
        </div>
        <ChevronRightIcon size={16} className="text-ink-dim shrink-0" />
      </button>
      {/* Day total */}
      <div className="card-elevated p-4 mb-6 border-emerald-glow/20">
        <div className="flex items-baseline justify-between">
          <span className="label">Всего за день</span>
          <span className="font-display text-2xl font-bold">
            {fmtNum(totals.calories)}
            <span className="text-sm text-ink-muted font-body ml-1">ккал</span>
          </span>
        </div>
        <div className="flex gap-4 text-xs mt-2">
          <span className="text-emerald-glow">Б {fmtNum(totals.protein, 1)}г</span>
          <span className="text-warn">Ж {fmtNum(totals.fat, 1)}г</span>
          <span className="text-sky-glow">У {fmtNum(totals.carbs, 1)}г</span>
        </div>
      </div>
      {loading ? (
        <Loader />
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <MealSection
              key={g.type}
              type={g.type}
              meals={g.items}
              expanded={expanded}
              onToggleExpand={toggleExpand}
              expandedComponents={expandedComponents}
              onToggleExpandComponents={toggleExpandComponents}
              onAdd={() => addToMealType(g.type)}
              onDelete={handleDelete}
              onDeleteItem={handleDeleteItem}
              onEditItem={setEditingItem}
              onOpenNutrients={openNutrients}
              thumbs={thumbs}
              onMealUpdated={refetch}
              partners={partners}
              onSend={(mealId) => { setSendSheet({ type: g.type, mealId }); setSendOpen(true); }}
            />
          ))}
        </div>
      )}
      <MealBreakdownModal
        open={nutrientsModal.open}
        onClose={() => setNutrientsModal({ ...nutrientsModal, open: false })}
        mealId={nutrientsModal.meal?.id ?? null}
        mealName={nutrientsModal.mealName}
        title={BREAKDOWN_TITLE[nutrientsModal.meal?.meal_type as string] ?? 'Разбор приёма'}
        onKickEnrich={() => {
          const m = nutrientsModal.meal;
          if (m) enrichMealNutrients(m.id).catch(() => {});
        }}
      />
      <SendToPartnerSheet
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        mealType={sendSheet?.type ?? 'breakfast'}
        meals={sendSheet ? meals.filter((m) => m.meal_type === sendSheet.type) : []}
        partners={partners}
        preselectMealId={sendSheet?.mealId ?? null}
      />
      <EditItemModal
        item={editingItem}
        onClose={() => setEditingItem(null)}
        onSaved={refetch}
      />
    </div>
  );
}
function MealSection({
  type,
  meals,
  expanded,
  onToggleExpand,
  expandedComponents,
  onToggleExpandComponents,
  onAdd,
  onDelete,
  onDeleteItem,
  onEditItem,
  onOpenNutrients,
  thumbs,
  onMealUpdated,
  partners,
  onSend,
}: {
  type: MealType;
  meals: Meal[];
  expanded: Record<string, boolean>;
  onToggleExpand: (id: string) => void;
  expandedComponents: Record<string, boolean>;
  onToggleExpandComponents: (itemId: string) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onDeleteItem: (itemId: string) => void;
  onEditItem: (item: any) => void;
  onOpenNutrients: (meal: Meal, data: NutrientsData | null) => void;
  thumbs: Record<string, string>;
  onMealUpdated: () => void;
  partners: Partner[];
  /** Открыть окно отправки: null — отмечено всё, id — продукты одной записи */
  onSend: (mealId: string | null) => void;
}) {
  const canSend = partners.length > 0 && meals.some((m) => (m.items ?? []).length > 0);
  const Icon = mealIcons[type];
  const colorClass = mealColors[type];
  const totalCal = meals.reduce((a, m) => a + m.total_calories, 0);
  const totalP = meals.reduce((a, m) => a + Number(m.total_protein_g || 0), 0);
  const totalF = meals.reduce((a, m) => a + Number(m.total_fat_g || 0), 0);
  const totalU = meals.reduce((a, m) => a + Number(m.total_carbs_g || 0), 0);
  return (
    <div className="card overflow-hidden">
      <div className="p-3 flex items-center gap-3 border-b border-line/50">
        <div className={`w-9 h-9 rounded-xl bg-bg-elevated flex items-center justify-center ${colorClass}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">
            {mealTypeLabel[type]}
          </div>
          {meals.length > 0 ? (
            <div className="text-[11px] text-ink-dim">
              {totalCal} ккал · Б{fmtNum(totalP, 0)} Ж{fmtNum(totalF, 0)} У{fmtNum(totalU, 0)}
            </div>
          ) : (
            <div className="text-[11px] text-ink-dim">Пока пусто</div>
          )}
        </div>
        {/* Отправить партнёру несколько продуктов разом — с выбором галочками (10.10.2026) */}
        {canSend && (
          <button
            onClick={() => onSend(null)}
            aria-label="Отправить партнёру"
            className="h-9 px-2.5 rounded-xl bg-sky-glow/10 text-sky-glow flex items-center gap-1.5 text-xs font-medium hover:bg-sky-glow/20 transition-colors active:scale-95 shrink-0"
          >
            {/* С подписью: один самолётик без слов не читался как «отправить» (Слава 10.10.2026) */}
            <Send size={14} />
            <span className="max-w-[72px] truncate">{partners.length === 1 ? partners[0].name || 'Отправить' : 'Отправить'}</span>
          </button>
        )}
        <button
          onClick={onAdd}
          className="w-9 h-9 rounded-xl bg-emerald-glow/10 text-emerald-glow flex items-center justify-center hover:bg-emerald-glow/20 transition-colors active:scale-95"
        >
          <Plus size={18} />
        </button>
      </div>
      {meals.length > 0 && (
        <div className="divide-y divide-line/30">
          {meals.map((m) => (
            <MealRow
              key={m.id}
              meal={m}
              expanded={!!expanded[m.id]}
              onToggleExpand={() => onToggleExpand(m.id)}
              expandedComponents={expandedComponents}
              onToggleExpandComponents={onToggleExpandComponents}
              onDelete={() => onDelete(m.id)}
              thumbUrl={(m as any).photo_url ? thumbs[(m as any).photo_url] : undefined}
              onDeleteItem={onDeleteItem}
              onEditItem={onEditItem}
              onOpenNutrients={onOpenNutrients}
              onMealUpdated={onMealUpdated}
              sendLabel={canSend ? (partners.length === 1 ? partners[0].name || 'партнёру' : 'партнёру') : null}
              onSend={() => onSend(m.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
function MealRow({
  meal,
  expanded,
  onToggleExpand,
  expandedComponents,
  onToggleExpandComponents,
  onDelete,
  thumbUrl,
  onDeleteItem,
  onEditItem,
  onOpenNutrients,
  onMealUpdated,
  sendLabel,
  onSend,
}: {
  meal: Meal;
  expanded: boolean;
  onToggleExpand: () => void;
  expandedComponents: Record<string, boolean>;
  onToggleExpandComponents: (itemId: string) => void;
  meal_id?: string;
  onDelete: () => void;
  thumbUrl?: string;
  onDeleteItem: (itemId: string) => void;
  onEditItem: (item: any) => void;
  onOpenNutrients: (meal: Meal, data: NutrientsData | null) => void;
  onMealUpdated: () => void;
  /** Имя партнёра для кнопки; null — партнёров нет, кнопку не показываем */
  sendLabel: string | null;
  onSend: () => void;
}) {
  const items = meal.items ?? [];
  const itemsList = items.map((i: any) => i.name).join(', ');
  const time = meal.consumed_at ? format(new Date(meal.consumed_at), 'HH:mm') : '';
  const cachedNutrients: NutrientsData | null = (meal as any).nutrients ?? null;
  const { fetchNutrients, loading: nutrientsLoading, data: fetchedData } = useNutrients();
  const [nutrientsRequested, setNutrientsRequested] = useState(false);
  const currentNutrients = cachedNutrients || fetchedData;
  const summaryKeywords = currentNutrients?.summary_keywords ?? [];
  /**
   * Открытие разбора (29.09.2026).
   *
   * Раньше здесь ждали ответ модели (`analyze-nutrients`), и человек смотрел
   * на спиннер «Анализирую пользу…». Теперь числа берутся из `meal_items`,
   * которые досчитал `enrich-meal`, поэтому карточка открывается сразу
   * и не стоит ничего. Кэш старых разборов оставлен для подписи-ключевых слов.
   */
  function handleOpenNutrients() {
    onOpenNutrients(meal, currentNutrients);
  }
  return (
    <div>
      <button
        onClick={onToggleExpand}
        className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-bg-elevated/50 transition-colors text-left"
      >
        <ChevronDown
          size={14}
          className={`text-ink-dim shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
        {thumbUrl && (
          <img
            src={thumbUrl}
            alt=""
            loading="lazy"
            className="w-9 h-9 rounded-lg object-cover shrink-0 border border-line/50"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-ink truncate">
            {itemsList || 'Без названия'}
          </div>
          <div className="text-[11px] text-ink-dim">
            {time && `${time} · `}{meal.total_calories} ккал
          </div>
        </div>
        <div className="text-[11px] text-ink-dim shrink-0">
          {items.length} {items.length === 1 ? 'продукт' : items.length < 5 ? 'продукта' : 'продуктов'}
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden bg-bg-elevated/30"
          >
            <div className="px-3 pb-3 pt-1 space-y-1.5">
              {/* Поделиться — В НАЧАЛЕ карточки (замечание Славы 05.09.2026:
                  «расположение в самом низу списка оправданно? может прям
                  вначале?»). Внизу её не видно: списки бывают по девять
                  позиций, а под ними ещё три действия. Тот же урок мы уже
                  проходили с «Отправить партнёру» — её тоже пришлось поднять.
                  Шапка приёма выше уже показывает тип, время и ккал, так что
                  контекста «чем делюсь» здесь достаточно. */}
              {items.length > 0 && <ShareMealButton mealId={meal.id} mealType={meal.meal_type} />}

              {items.length === 0 ? (
                <div className="text-xs text-ink-dim text-center py-2">
                  Нет данных о составе
                </div>
              ) : (
                items.map((item: any, idx: number) => {
                  // 🆕 Проверяем есть ли components у этого item
                  const hasComponents = Array.isArray(item.components) && item.components.length > 0;
                  const isComponentsExpanded = item.id ? !!expandedComponents[item.id] : false;
                  return (
                    <ItemRow
                      key={item.id ?? idx}
                      item={item}
                      hasComponents={hasComponents}
                      isComponentsExpanded={isComponentsExpanded}
                      onToggleComponents={() => item.id && onToggleExpandComponents(item.id)}
                      onEditItem={onEditItem}
                      onDeleteItem={onDeleteItem}
                    />
                  );
                })
              )}
              {/* Отправить партнёру — сразу под составом, пока приём перед глазами.
                  Внизу карточки, после «Пользы» и подсказок, кнопку приходилось искать. */}
              {items.length > 0 && sendLabel && (
                <button
                  onClick={onSend}
                  className="w-full mt-2 py-1.5 rounded-lg text-xs text-sky-glow bg-sky-glow/5 border border-sky-glow/20 hover:bg-sky-glow/10 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Send size={11} /> Отправить → {sendLabel}
                </button>
              )}

              {/* Возрастная подсказка по приёму пищи */}
              {items.length > 0 && (
                <MealHintBlock
                  mealId={meal.id}
                  totalProteinG={Number(meal.total_protein_g) || 0}
                  totalCaloriesKcal={meal.total_calories || 0}
                  onAdded={onMealUpdated}
                />
              )}
              {/* Блок Польза */}
              {items.length > 0 && (
                <>
                  {summaryKeywords.length > 0 ? (
                    <NutrientsBadge
                      keywords={summaryKeywords}
                      onClick={handleOpenNutrients}
                    />
                  ) : (
                    <button
                      onClick={handleOpenNutrients}
                      className="w-full mt-2 py-2 rounded-lg text-xs text-emerald-glow bg-emerald-glow/5 border border-emerald-glow/20 hover:bg-emerald-glow/10 transition-colors flex items-center justify-center gap-1.5"
                    >
                      ✨ {BREAKDOWN_CTA[meal.meal_type as string] ?? 'Разобрать приём'}
                    </button>
                  )}
                </>
              )}
              <button
                onClick={onDelete}
                className="w-full mt-2 py-1.5 rounded-lg text-xs text-danger hover:bg-danger/10 transition-colors flex items-center justify-center gap-1.5"
              >
                <Trash2 size={11} />
                Удалить весь приём
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
// 🆕 Отдельный компонент строки продукта с поддержкой components
function ItemRow({
  item,
  hasComponents,
  isComponentsExpanded,
  onToggleComponents,
  onEditItem,
  onDeleteItem,
}: {
  item: any;
  hasComponents: boolean;
  isComponentsExpanded: boolean;
  onToggleComponents: () => void;
  onEditItem: (item: any) => void;
  onDeleteItem: (itemId: string) => void;
}) {
  // Правка продукта: название, вес, КБЖУ, состав. Components передаём целиком.
  function openEdit() {
    onEditItem({
      id: item.id,
      name: item.name,
      weight_g: Number(item.weight_g) || 0,
      calories: Number(item.calories) || 0,
      protein_g: Number(item.protein_g) || 0,
      fat_g: Number(item.fat_g) || 0,
      carbs_g: Number(item.carbs_g) || 0,
      components: Array.isArray(item.components) ? item.components : [],
    });
  }
  return (
    <div className="space-y-1.5">
      {/* Основная строка продукта */}
      <div className="flex items-start justify-between gap-2 text-xs group">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {/* Тап по названию открывает правку: переименовать продукт можно и после
                записи (11.10.2026). Безымянному — явная подсказка. */}
            <button
              type="button"
              disabled={!item.id}
              onClick={(e) => { e.stopPropagation(); openEdit(); }}
              className="text-ink truncate text-left min-w-0"
            >
              {item.name}
            </button>
            {item.id && (!item.name?.trim() || item.name.trim().toLowerCase() === 'продукт') && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openEdit(); }}
                className="shrink-0 px-1.5 py-0.5 rounded-full bg-warn/10 text-warn text-[9px] font-medium"
              >
                дать название
              </button>
            )}
            {/* 🆕 Бейджик "состав" если есть components */}
            {hasComponents && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleComponents();
                }}
                className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-glow/10 text-sky-glow text-[9px] font-medium hover:bg-sky-glow/20 transition-colors"
                title={isComponentsExpanded ? 'Скрыть состав' : 'Показать состав'}
              >
                <span>состав</span>
                <ChevronDown
                  size={9}
                  className={`transition-transform ${isComponentsExpanded ? 'rotate-180' : ''}`}
                />
              </button>
            )}
          </div>
          <div className="flex gap-2 text-[10px] mt-0.5">
            <span className="text-ink-dim">{item.weight_g}г</span>
            <span className="text-emerald-glow">Б{fmtNum(Number(item.protein_g), 0)}</span>
            <span className="text-warn">Ж{fmtNum(Number(item.fat_g), 0)}</span>
            <span className="text-sky-glow">У{fmtNum(Number(item.carbs_g), 0)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-ink font-medium text-right mr-1">
            {item.calories} ккал
          </span>
          {item.id && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit();
                }}
                className="w-6 h-6 rounded-md text-ink-dim hover:text-emerald-glow hover:bg-emerald-glow/10 flex items-center justify-center transition-colors"
                title="Редактировать"
              >
                <Edit3 size={11} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Удалить «${item.name}»?`)) {
                    onDeleteItem(item.id);
                  }
                }}
                className="w-6 h-6 rounded-md text-ink-dim hover:text-danger hover:bg-danger/10 flex items-center justify-center transition-colors"
                title="Удалить продукт"
              >
                <Trash2 size={11} />
              </button>
            </>
          )}
        </div>
      </div>
      {/* 🆕 Развёрнутый состав (components) */}
      <AnimatePresence>
        {hasComponents && isComponentsExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="ml-3 pl-3 border-l-2 border-sky-glow/30 space-y-1 py-1">
              <div className="text-[9px] text-ink-dim uppercase tracking-wider mb-1">
                Состав по версии AI
              </div>
              {(item.components as MealComponent[]).map((c, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 text-[10px] py-0.5"
                >
                  <span className="text-ink-muted flex-1 min-w-0 truncate">• {c.name}</span>
                  <span className="text-ink-dim shrink-0">
                    {c.weight_g}г · {c.calories} ккал
                  </span>
                </div>
              ))}
              <div className="text-[9px] text-ink-dim italic pt-1">
                💡 Тапни ✎ чтобы изменить состав
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
